import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { logoutUser } from './react-redux/Slicer/logoutUser';
import { initializeAuth, deleteUser } from './react-redux/Slicer/userSlice';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";

import "./App.css";
import { startAuthMonitoring, stopAuthMonitoring } from './utils/authMonitor';
import { SessionProvider } from './contexts/SessionContext';
import {
  startCrossTabActivityTracking,
  isInactive,
  clearActivityTracking
} from './utils/crossTabActivity';

// ── T2T Module ────────────────────────────────────────────────────────────────
import T2TLayout       from "./components/T2T/Layout/T2TLayout.js";
import T2TDashboard    from "./components/T2T/Dashboard/T2TDashboard.js";
import T2TBuilder      from "./components/T2T/Builder/T2TBuilder.js";
import T2TQueue        from "./components/T2T/Queue/T2TQueue.js";
import T2TRequestDetail  from "./components/T2T/Requests/T2TRequestDetail.js";
import RequestsPage      from "./components/T2T/Requests/RequestsPage.js";
import NotificationsPage           from "./components/T2T/Notifications/NotificationsPage.js";
import NotificationPreferencesPage from "./components/T2T/Notifications/NotificationPreferencesPage.js";
import T2TSubmit       from "./components/T2T/Submit/T2TSubmit.js";
import T2TOrgSetup     from "./components/T2T/OrgSetup/T2TOrgSetup.js";
import T2TAdmin        from "./components/T2T/Admin/T2TAdmin.js";
import T2TReports      from "./components/T2T/Reports/T2TReports.js";
import T2TIntegrations from "./components/T2T/Integrations/T2TIntegrations.js";
import T2TChangePassword from "./components/T2T/Auth/T2TChangePassword.js";
import T2TOnboarding     from "./components/T2T/Onboarding/T2TOnboarding.js";
import T2TVendorPortal   from "./components/T2T/Vendor/T2TVendorPortal.js";
import VendorPortalChat  from "./components/T2T/Vendor/VendorPortalChat.js";
import T2TVendorSignup   from "./components/T2T/Vendor/T2TVendorSignup.js";
import T2TVendorList     from "./components/T2T/Vendor/T2TVendorList.js";
import T2TVendorDetail   from "./components/T2T/Vendor/T2TVendorDetail.js";

// ── Shared shell + auth ───────────────────────────────────────────────────────
import NotFound from "./components/Assets/NotFound.js";
import Login from "./components/Login/Login.js";
import MainLayout from "./components/Layout/MainLayout.js";
import AuthProvider from "./components/Auth/AuthProvider.js";
import TermsAndCondition from "./components/Login/TermsAndCondition.js";
import PrivacyPolicy from "./components/Login/PrivacyPolicy.js";
import SsoCallback from "./components/Login/SsoCallback.js";
import NetworkStatusNotifier from './components/Assets/NetworkStatusNotifier.js';

// ── User management (admin) — folded into the T2T app ─────────────────────────
import AddUser from "./components/Forms/Add User.js";
import EditUser from "./components/Forms/Edit User.js";
import ViewUsers from "./components/Listings/View_Users.js";
import ViewCompanies from "./components/Listings/View_Company.js";
import ViewLocations from "./components/Listings/View_Location.js";
import ViewDepartments from "./components/Listings/View_Departments.js";
import ViewSections from "./components/Listings/View_Sections.js";
import ViewSubSections from "./components/Listings/View_Sub_Sections.js";

// Guards T2T + admin routes — requires a logged-in session (T2T-only app).
const T2TRouteGuard = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/" replace />;
  return children;
};

T2TRouteGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

function App() {
  // Inactivity timeout in milliseconds (30 minutes)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
  const dispatch = useDispatch();
  const inactivityCheckIntervalRef = useRef();

  // Handler to logout user using logoutUser thunk
  const handleLogout = useCallback(() => {
    if (process.env.REACT_APP_DOMAIN_URL === 'https://localhost:3000') return; // Skip logout in local development

    clearActivityTracking();
    dispatch(logoutUser());
    window.location.href = '/';
  }, [dispatch]);

  // Check for inactivity across all tabs
  const checkInactivity = useCallback(() => {
    if (isInactive(INACTIVITY_TIMEOUT)) {
      handleLogout();
    }
  }, [handleLogout, INACTIVITY_TIMEOUT]);

  useEffect(() => {
    // Initialize auth on mount
    dispatch(initializeAuth());

    // Start global auth monitoring across tabs
    startAuthMonitoring(() => {
      dispatch(deleteUser());
      clearActivityTracking();
    });

    // Start cross-tab activity tracking
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const stopActivityTracking = startCrossTabActivityTracking(events);

    // Check for inactivity every 1 minute
    inactivityCheckIntervalRef.current = setInterval(checkInactivity, 60000);
    checkInactivity();

    return () => {
      stopActivityTracking();
      if (inactivityCheckIntervalRef.current) {
        clearInterval(inactivityCheckIntervalRef.current);
      }
      stopAuthMonitoring();
    };
  }, [checkInactivity, dispatch]);

  return (
    <Router>
      <SessionProvider>
        <AuthProvider>
          <NetworkStatusNotifier />
          <Routes basename="/">
            <Route element={<MainLayout />}>
              {/* Public routes - no authentication required */}
              <Route path="/" element={<Login />} />
              <Route path="/terms-and-condition" element={<TermsAndCondition />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* Plot OS SSO handoff receiver (fragment-based) */}
              <Route path="/sso-callback" element={<SsoCallback />} />

              {/* T2T — Standalone pages (no sidebar/layout) */}
              <Route path="/t2t-change-password" element={<T2TChangePassword />} />
              <Route path="/t2t-onboarding" element={<T2TOnboarding />} />
              <Route path="/vendor-portal" element={<T2TVendorPortal />} />
              {/* T2T-05-C6: public vendor portal chat — token-authenticated */}
              <Route path="/vendor-portal/chat" element={<VendorPortalChat />} />
              {/* ARCH-03: public vendor self-registration (no auth) */}
              <Route path="/vendor-signup" element={<T2TVendorSignup />} />

              {/* T2T Module + folded-in User Management — require login */}
              <Route element={<T2TRouteGuard><T2TLayout /></T2TRouteGuard>}>
                <Route path="/t2t-home"            element={<T2TDashboard />} />
                <Route path="/t2t-builder/:id"     element={<T2TBuilder />} />
                <Route path="/t2t-queue"           element={<T2TQueue />} />
                <Route path="/t2t-requests"        element={<Navigate to={{ pathname: "/t2t/requests", search: window.location.search }} replace />} />
                <Route path="/t2t/requests"        element={<RequestsPage />} />
                <Route path="/t2t/notifications"             element={<NotificationsPage />} />
                <Route path="/t2t/notifications/preferences" element={<NotificationPreferencesPage />} />
                <Route path="/t2t-request/:id"     element={<T2TRequestDetail />} />
                <Route path="/t2t-submit/:wfId"    element={<T2TSubmit />} />
                <Route path="/t2t-org"             element={<T2TOrgSetup />} />
                <Route path="/t2t-admin"           element={<T2TAdmin />} />
                <Route path="/t2t-reports"         element={<T2TReports />} />
                <Route path="/t2t-integrations"    element={<T2TIntegrations />} />
                <Route path="/t2t-vendors"         element={<T2TVendorList />} />
                <Route path="/t2t-vendor/:id"      element={<T2TVendorDetail />} />

                {/* User management (admin) — reached from the T2T sidebar */}
                <Route path="/view-users"        element={<ViewUsers />} />
                <Route path="/add-user"          element={<AddUser />} />
                <Route path="/edit-user"         element={<EditUser />} />
                <Route path="/view-companies"    element={<ViewCompanies />} />
                <Route path="/view-locations"    element={<ViewLocations />} />
                <Route path="/view-departments"  element={<ViewDepartments />} />
                <Route path="/view-sections"     element={<ViewSections />} />
                <Route path="/view-sub-sections" element={<ViewSubSections />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </AuthProvider>
      </SessionProvider>
    </Router>
  );
}

export default App;
